import {
	Button,
	ButtonProps,
	chakra,
	HTMLChakraProps,
	Menu,
	MenuButton,
	MenuItem,
	MenuItemProps,
	MenuList,
	MenuListProps,
	MenuProps,
	Portal,
	useDisclosure,
	useMultiStyleConfig,
	usePopper,
} from "@chakra-ui/react";
import React, { ReactNode, useCallback, useRef } from "react";
import { NextLink } from "src/components/NextLink";
import {
	HeaderDropdownContextProvider,
	useHeaderDropdownContext,
} from "./context";
import type { LinkProps } from "next/link";

type HeaderDropdownItemProps = {
	href?: LinkProps["href"];
	isExternal?: boolean;
	label: ReactNode;
	variant?: string;
} & MenuItemProps;

export const HeaderDropdownItem: React.FC<HeaderDropdownItemProps> = ({
	href,
	onClick,
	label,
	icon,
	isExternal = false,
	...rest
}) => {
	const styles = useMultiStyleConfig("HeaderDropdown", {
		variant: icon ? "withArrow" : undefined,
	});

	const handleClick = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			onClick?.(e);
		},
		[onClick],
	);

	// In AuthHeader for example, some menuItems call openModal via their onClick
	if (!href) {
		return (
			<MenuItem
				icon={icon}
				onClick={handleClick}
				sx={styles.menuItem}
				{...rest}
			>
				{label}
			</MenuItem>
		);
	}

	if (isExternal) {
		return (
			<MenuItem
				as="a"
				icon={icon}
				onClick={handleClick}
				sx={styles.menuItem}
				href={href}
				{...rest}
			>
				{label}
			</MenuItem>
		);
	}

	return (
		<NextLink href={`/${href}`} passHref>
			<MenuItem
				as="a"
				icon={icon}
				onClick={handleClick}
				sx={styles.menuItem}
				{...rest}
			>
				{label}
			</MenuItem>
		</NextLink>
	);
};

export const HeaderDropdownButton: React.FC<ButtonProps> = ({
	children,
	onClick,
	onMouseEnter,
	...rest
}) => {
	const styles = useMultiStyleConfig("HeaderDropdown");
	const { openOnHover, onOpen } = useHeaderDropdownContext();

	const handleMouseEnter = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			if (openOnHover) {
				onOpen();
			}
			onMouseEnter?.(e);
		},
		[openOnHover, onOpen, onMouseEnter],
	);

	const handleClick = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			// For hover-menus a pointer "click" is preceded by the hover that
			// already opened the menu. Chakra wires the button as
			// `callAllHandlers(props.onClick, onToggle)`, so without this the
			// trailing `onToggle` would immediately close it again — making the
			// open state non-deterministic (and the e2e tests flaky). We open
			// explicitly and `preventDefault()` to short-circuit Chakra's
			// `onToggle`. Closing happens via item-select / outside-click /
			// Escape. Click-to-toggle stays intact for `openOnHover={false}`.
			if (openOnHover) {
				onOpen();
				e.preventDefault();
			}
			onClick?.(e);
		},
		[openOnHover, onOpen, onClick],
	);

	return (
		<MenuButton
			data-testid="header-dropdown-button"
			as={Button}
			sx={styles.button}
			onMouseEnter={handleMouseEnter}
			onClick={handleClick}
			{...rest}
		>
			{children}
		</MenuButton>
	);
};

export const MenuArrow: React.FC<HTMLChakraProps<"div">> = (props) => {
	const { getArrowProps, getArrowInnerProps } = usePopper();

	return (
		<chakra.div
			{...getArrowProps({
				shadowColor: "lightgray",
				size: "1rem",
				bg: "white",
				zIndex: "1 !important",
			})}
			{...props}
		>
			<chakra.div {...getArrowInnerProps({})} />
		</chakra.div>
	);
};

export const HeaderDropdownContent: React.FC<MenuListProps> = ({
	children,
	...rest
}) => {
	const styles = useMultiStyleConfig("HeaderDropdown");
	const menuListRef = useRef<HTMLDivElement>(null);

	return (
		<Portal>
			<MenuList
				ref={menuListRef}
				sx={styles.menuList}
				zIndex="dropdown"
				{...rest}
			>
				{children}
			</MenuList>
		</Portal>
	);
};

export const HeaderDropdown: React.FC<
	MenuProps & { openOnHover?: boolean }
> = ({ children, openOnHover = true, ...rest }) => {
	const { isOpen, onOpen, onClose } = useDisclosure();

	return (
		<HeaderDropdownContextProvider value={{ openOnHover, onOpen, onClose }}>
			<Menu {...rest} isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
				{children}
			</Menu>
		</HeaderDropdownContextProvider>
	);
};

// 🔬 TBD: Please evaluate
